const RELOAD_GUARD_KEY = "wgi_chunk_reload_at"
const RELOAD_RECOVERY_KEY = "wgi_chunk_reload_recovery"
const RELOAD_COOLDOWN_MS = 30_000
const RECOVERY_MAX_AGE_MS = 5 * 60_000

export interface ChunkReloadRecovery {
  source: string
  path: string
  elapsedMs: number
}

export function isChunkLoadError(value: unknown): boolean {
  if (!value || typeof value !== "object") return false
  const v = value as { name?: unknown; message?: unknown }
  if (v.name === "ChunkLoadError") return true
  return (
    typeof v.message === "string" &&
    /Loading chunk [^\s]+ failed|Failed to load chunk/i.test(v.message)
  )
}

/**
 * True when no chunk reload has fired in the last 30s. Claims the slot as a
 * side effect, so a caller that gets `true` must actually reload: the cooldown
 * is what stops an infinite refresh loop when the new deploy is itself broken.
 */
export function claimChunkReload(source = "unknown"): boolean {
  try {
    const now = Date.now()
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || "0")
    if (now - last <= RELOAD_COOLDOWN_MS) return false
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now))
    sessionStorage.setItem(RELOAD_RECOVERY_KEY, JSON.stringify({
      source,
      path: typeof window === "undefined" ? "unknown" : window.location.pathname,
      attemptedAt: now,
    }))
  } catch {
    // sessionStorage may be unavailable (Safari private mode) — fall through
  }
  return true
}

/**
 * Read and clear a recent reload marker after the new page has mounted. A
 * successful mount is the observable proof that the cache-busted reload
 * recovered the stale chunk failure.
 */
export function consumeChunkReloadRecovery(): ChunkReloadRecovery | null {
  try {
    const raw = sessionStorage.getItem(RELOAD_RECOVERY_KEY)
    if (!raw) return null
    sessionStorage.removeItem(RELOAD_RECOVERY_KEY)

    const marker = JSON.parse(raw) as {
      source?: unknown
      path?: unknown
      attemptedAt?: unknown
    }
    const now = Date.now()
    if (
      typeof marker.source !== "string" ||
      typeof marker.path !== "string" ||
      typeof marker.attemptedAt !== "number" ||
      marker.attemptedAt > now + 5_000 ||
      now - marker.attemptedAt > RECOVERY_MAX_AGE_MS
    ) {
      return null
    }

    return {
      source: marker.source,
      path: marker.path,
      elapsedMs: Math.max(0, now - marker.attemptedAt),
    }
  } catch {
    return null
  }
}

export function chunkReloadUrl(href: string): string {
  const url = new URL(href)
  url.searchParams.set("_r", String(Date.now()))
  return url.toString()
}

export function reloadWithCacheBuster() {
  window.location.replace(chunkReloadUrl(window.location.href))
}

export function reloadOnceForChunkError(source = "unknown"): boolean {
  if (!claimChunkReload(source)) return false
  reloadWithCacheBuster()
  return true
}
